import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import Button from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import UnifiedAnamnesisForm from '../components/intake/UnifiedAnamnesisForm'
import { initialAnamnesisForm, normalizeSupportQuestionnaire } from '../components/intake/anamnesisSchema'
import { buildLocalTutorReply } from '../services/localTutor'
import SupportVideoRoom from '../components/support/SupportVideoRoom'

const ROOMS_KEY = 'mathkids.localSupportRooms'
const MESSAGES_KEY = 'mathkids.localSupportMessages'
const ANAMNESIS_KEY = 'mathkids.anamnesisByProfessional'
const STATUS_LABELS = {
  open: 'Em conversa',
  waiting: 'Aguardando profissional',
  closed: 'Concluído'
}

const HIDDEN_SYSTEM_PREFIXES = [
  'Sala criada para atendimento',
  'Triagem inicial registrada',
  'Status atualizado para',
  'Fluxo profissional atualizado:'
]

function shouldHideEntry(entry) {
  const role = String(entry?.senderRole || '').toLowerCase()
  const message = String(entry?.message || '').trim()
  return role === 'system' && HIDDEN_SYSTEM_PREFIXES.some((prefix) => message.startsWith(prefix))
}

function getVisibleSenderLabel(entry, user) {
  const role = String(entry?.senderRole || '').toLowerCase()
  if (role === 'tutor_ia') return 'Tutor IA'
  if (role === 'professional') return 'Profissional'
  if (role === 'admin') return 'Equipe'
  if (['parent', 'student', 'teacher'].includes(role)) return 'Você'
  if (role === 'system') return ''
  if (role === String(user?.userType || '').toLowerCase()) return 'Você'
  return ''
}

function readLocal(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback))
  } catch {
    return fallback
  }
}

function writeLocal(key, value) {
  localStorage.setItem(key, JSON.stringify(value))
}

function getProfessionalKey({ professionalName, professionalId }) {
  return String(professionalId || professionalName || 'geral')
}


function getLocalSenderRole(user) {
  const normalized = String(user?.userType || '').trim().toLowerCase()
  if (['student', 'parent', 'teacher', 'professional', 'admin'].includes(normalized)) return normalized
  return 'parent'
}

export default function SupportPage({ user }) {
  const [searchParams] = useSearchParams()
  const [rooms, setRooms] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [questionnaire, setQuestionnaire] = useState(initialAnamnesisForm)
  const [title, setTitle] = useState('Atendimento on-line')
  const [newMessage, setNewMessage] = useState('')
  const [message, setMessage] = useState('')
  const [busyStatus, setBusyStatus] = useState(false)
  const professionalName = useMemo(() => searchParams.get('professional') || '', [searchParams])
  const specialty = useMemo(() => searchParams.get('specialty') || '', [searchParams])
  const professionalId = useMemo(() => searchParams.get('professionalId') || '', [searchParams])
  const forceAnamnesis = useMemo(() => searchParams.get('forceAnamnesis') === '1', [searchParams])
  const professionalKey = useMemo(() => getProfessionalKey({ professionalName, professionalId }), [professionalName, professionalId])
  const savedAnamnesis = useMemo(() => readLocal(ANAMNESIS_KEY, {}), [])
  const hasPreviousAnamnesis = Boolean(savedAnamnesis[professionalKey])
  const shouldShowQuestionnaire = forceAnamnesis || !hasPreviousAnamnesis

  useEffect(() => {
    const requestedTitle = searchParams.get('title')
    if (requestedTitle) setTitle(requestedTitle)
    if (searchParams.get('grade')) setQuestionnaire((current) => ({ ...current, schoolYear: searchParams.get('grade') || current.schoolYear }))
  }, [searchParams])

  useEffect(() => { fetchRooms() }, [user])
  useEffect(() => { if (selectedRoom?.id) fetchMessages(selectedRoom.id) }, [selectedRoom?.id])

  const fetchRooms = async () => {
    if (!user) {
      const localRooms = readLocal(ROOMS_KEY, [])
      setRooms(localRooms)
      if (localRooms.length && !selectedRoom) setSelectedRoom(localRooms[0])
      if (!localRooms.length) setMessage('Você pode iniciar o atendimento agora mesmo. Sem login, a sala fica salva no navegador.')
      return
    }
    try {
      const { data } = await api.get('/support/rooms')
      const loadedRooms = data.rooms || []
      setRooms(loadedRooms)
      if (loadedRooms.length && !selectedRoom) setSelectedRoom(loadedRooms[0])
      setMessage('')
    } catch {
      const localRooms = readLocal(ROOMS_KEY, [])
      setRooms(localRooms)
      if (localRooms.length && !selectedRoom) setSelectedRoom(localRooms[0])
      setMessage('API indisponível. Atendimento aberto em modo local para não travar o portal.')
    }
  }

  const fetchMessages = async (roomId) => {
    if (!user || String(roomId).startsWith('local-')) {
      const localMessages = readLocal(MESSAGES_KEY, {})
      setMessages(localMessages[roomId] || [])
      return
    }
    try {
      const { data } = await api.get(`/support/rooms/${roomId}/messages`)
      setMessages(data.messages || [])
    } catch {
      const localMessages = readLocal(MESSAGES_KEY, {})
      setMessages(localMessages[roomId] || [])
      setMessage('Mensagens carregadas em modo local.')
    }
  }

  const persistAnamnesis = () => {
    const all = readLocal(ANAMNESIS_KEY, {})
    all[professionalKey] = {
      professionalName,
      professionalId,
      specialty,
      completedAt: new Date().toISOString(),
      questionnaire: normalizeSupportQuestionnaire(questionnaire)
    }
    writeLocal(ANAMNESIS_KEY, all)
  }

  const createLocalRoom = () => {
    const roomId = `local-${Date.now()}`
    const normalizedQuestionnaire = normalizeSupportQuestionnaire(questionnaire)
    const room = {
      id: roomId,
      title,
      createdAt: new Date().toISOString(),
      status: 'waiting',
      preQuestionnaire: { optional: true, ...normalizedQuestionnaire },
      professionalName,
      professionalId,
      specialty,
      anamnesisCompleted: shouldShowQuestionnaire ? true : hasPreviousAnamnesis
    }
    const currentRooms = readLocal(ROOMS_KEY, [])
    const nextRooms = [room, ...currentRooms]
    writeLocal(ROOMS_KEY, nextRooms)
    const intro = {
      id: `${roomId}-welcome`,
      senderRole: 'system',
      message: professionalName
        ? `Sala criada para atendimento com ${professionalName}${specialty ? ` • ${specialty}` : ''}. Status inicial: aguardando profissional.`
        : 'Sala criada para atendimento on-line.'
    }
    const tutorIntro = {
      id: `${roomId}-tutor`,
      senderRole: 'tutor_ia',
      message: buildLocalTutorReply({ message: title, questionnaire: normalizedQuestionnaire, title })
    }
    const triage = {
      id: `${roomId}-triage`,
      senderRole: 'system',
      message: `Triagem inicial registrada para ${normalizedQuestionnaire.studentName || 'o estudante'} • série ${normalizedQuestionnaire.grade || 'não informada'} • metas: ${normalizedQuestionnaire.familyGoals || 'em definição'}.`
    }
    const allMessages = readLocal(MESSAGES_KEY, {})
    allMessages[roomId] = [intro, triage, tutorIntro]
    writeLocal(MESSAGES_KEY, allMessages)
    setRooms(nextRooms)
    setSelectedRoom(room)
    setMessages(allMessages[roomId])
    setQuestionnaire(initialAnamnesisForm)
    setTitle('Atendimento on-line')
    setMessage(shouldShowQuestionnaire ? 'Questionário unificado salvo e atendimento iniciado.' : 'Atendimento iniciado com anamnese já existente para esse profissional.')
  }

  const createRoom = async () => {
    if (shouldShowQuestionnaire) persistAnamnesis()
    if (!user) {
      createLocalRoom()
      return
    }
    try {
      const payload = {
        title,
        professionalId: professionalId || null,
        questionnaire: { optional: true, ...normalizeSupportQuestionnaire(questionnaire), professionalName, specialty },
        anamnesisRequired: shouldShowQuestionnaire
      }
      const { data } = await api.post('/support/rooms', payload)
      setQuestionnaire(initialAnamnesisForm)
      setTitle('Atendimento on-line')
      setSelectedRoom(data.room)
      setMessage(shouldShowQuestionnaire ? 'Sala criada. O questionário foi salvo antes do atendimento.' : 'Sala criada com reaproveitamento da anamnese anterior.')
      await fetchRooms()
    } catch {
      createLocalRoom()
      setMessage('API indisponível. Atendimento criado em modo local.')
    }
  }

  const ensureLocalMirrorRoom = (room) => {
    const localRooms = readLocal(ROOMS_KEY, [])
    const existing = localRooms.find((item) => item.id === room.id)
    if (existing) return localRooms
    const nextRooms = [room, ...localRooms]
    writeLocal(ROOMS_KEY, nextRooms)
    return nextRooms
  }

  const updateLocalRoomStatus = (roomId, status) => {
    const localRooms = readLocal(ROOMS_KEY, [])
    const nextRooms = localRooms.map((room) => room.id === roomId ? { ...room, status } : room)
    writeLocal(ROOMS_KEY, nextRooms)
    setRooms(nextRooms)
    setSelectedRoom((current) => current?.id === roomId ? { ...current, status } : current)
    const allMessages = readLocal(MESSAGES_KEY, {})
    const nextMessages = [...(allMessages[roomId] || []), { id: `${roomId}-status-${Date.now()}`, senderRole: 'system', message: `Status atualizado para ${STATUS_LABELS[status] || status}.` }]
    allMessages[roomId] = nextMessages
    writeLocal(MESSAGES_KEY, allMessages)
    setMessages(nextMessages)
  }

  const updateRoomStatus = async (status) => {
    if (!selectedRoom?.id) return
    if (!user || String(selectedRoom.id).startsWith('local-')) {
      updateLocalRoomStatus(selectedRoom.id, status)
      return
    }
    try {
      setBusyStatus(true)
      const { data } = await api.patch(`/support/rooms/${selectedRoom.id}/status`, { status })
      setSelectedRoom(data.room)
      setMessages(data.messages || messages)
      await fetchRooms()
    } catch {
      const mirrorRoom = { ...selectedRoom, id: `local-copy-${selectedRoom.id}` }
      ensureLocalMirrorRoom(mirrorRoom)
      setSelectedRoom(mirrorRoom)
      updateLocalRoomStatus(mirrorRoom.id, status)
      setMessage('API indisponível. O fluxo profissional continuou em modo local.')
    } finally {
      setBusyStatus(false)
    }
  }

  const sendMessage = async () => {
    if (!selectedRoom?.id || !newMessage.trim()) return
    const normalizedQuestionnaire = selectedRoom.preQuestionnaire || normalizeSupportQuestionnaire(questionnaire)
    if (!user || String(selectedRoom.id).startsWith('local-')) {
      const newEntry = { id: `${selectedRoom.id}-${Date.now()}`, senderRole: getLocalSenderRole(user), message: newMessage.trim() }
      const assistantEntry = { id: `${selectedRoom.id}-${Date.now()}-assistant`, senderRole: 'tutor_ia', message: buildLocalTutorReply({ message: newMessage.trim(), questionnaire: normalizedQuestionnaire, title: selectedRoom.title }) }
      const allMessages = readLocal(MESSAGES_KEY, {})
      const next = [...(allMessages[selectedRoom.id] || []), newEntry, assistantEntry]
      allMessages[selectedRoom.id] = next
      writeLocal(MESSAGES_KEY, allMessages)
      setMessages(next)
      setNewMessage('')
      return
    }
    try {
      const { data } = await api.post(`/support/rooms/${selectedRoom.id}/messages`, { message: newMessage })
      setMessages(data.messages || [])
      setNewMessage('')
    } catch {
      const fallbackRoom = { ...selectedRoom, id: `local-copy-${selectedRoom.id}` }
      ensureLocalMirrorRoom(fallbackRoom)
      setSelectedRoom(fallbackRoom)
      const allMessages = readLocal(MESSAGES_KEY, {})
      const next = [
        ...(allMessages[fallbackRoom.id] || []),
        { id: `${fallbackRoom.id}-${Date.now()}`, senderRole: getLocalSenderRole(user), message: newMessage.trim() },
        { id: `${fallbackRoom.id}-${Date.now()}-assistant`, senderRole: 'tutor_ia', message: buildLocalTutorReply({ message: newMessage.trim(), questionnaire: normalizedQuestionnaire, title: fallbackRoom.title }) }
      ]
      allMessages[fallbackRoom.id] = next
      writeLocal(MESSAGES_KEY, allMessages)
      setMessages(next)
      setNewMessage('')
      setMessage('API indisponível. A conversa continuou em modo local.')
    }
  }

  return (
    <div className="page-stack">
      <section className="section-title-wrap">
        <span className="eyebrow">Atendimento on-line</span>
        <h1>Triagem unificada, status profissional e conversa contínua.</h1>
        <p>O atendimento agora usa a mesma anamnese do agendamento, com reuso por profissional e fluxo mais claro entre triagem, espera e conclusão.</p>
        {professionalName ? <p><strong>Profissional selecionado:</strong> {professionalName}{specialty ? ` • ${specialty}` : ''}</p> : null}
      </section>

      {message ? <div className="alert info">{message}</div> : null}
      {!shouldShowQuestionnaire ? <div className="alert info">A anamnese deste profissional já existe. Ela só reaparece se o profissional solicitar nova aplicação.</div> : null}

      <div className="grid cols-2">
        <Card>
          <CardHeader title="Abrir nova sala" subtitle={shouldShowQuestionnaire ? 'Triagem antes do atendimento' : 'Atendimento com anamnese já registrada'} />
          <label className="form-label">Título da sala</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          {shouldShowQuestionnaire ? (
            <UnifiedAnamnesisForm form={questionnaire} setForm={setQuestionnaire} mode="compact" selectedProfessionalName={professionalName} />
          ) : <p>Usando a anamnese já salva para este profissional.</p>}
          <div className="button-wrap">
            <Button onClick={createRoom}>Criar sala</Button>
          </div>
        </Card>

        <Card>
          <CardHeader title="Salas abertas" subtitle="Escolha a conversa para continuar" />
          <div className="stack-sm">
            {rooms.map((room) => (
              <button key={room.id} type="button" className={`surface-card compact ${selectedRoom?.id === room.id ? 'chip-active' : ''}`} onClick={() => setSelectedRoom(room)}>
                <strong>{room.title}</strong>
                <p>Status: {STATUS_LABELS[room.status] || room.status || 'Em conversa'}</p>
                {room.professionalName ? <p>{room.professionalName}</p> : null}
              </button>
            ))}
            {!rooms.length ? <p>Nenhuma sala criada ainda.</p> : null}
          </div>
        </Card>
      </div>

      {selectedRoom ? (
        <>
        <SupportVideoRoom room={selectedRoom} user={user} />
        <Card>
          <CardHeader title={selectedRoom.title} subtitle={`Status atual: ${STATUS_LABELS[selectedRoom.status] || selectedRoom.status || 'Em conversa'}`} />
          <div className="button-wrap mb-6">
            <Button variant="secondary" disabled={busyStatus} onClick={() => updateRoomStatus('waiting')}>Solicitar retorno profissional</Button>
            <Button variant="ghost" disabled={busyStatus} onClick={() => updateRoomStatus('open')}>Marcar conversa ativa</Button>
            <Button variant="danger" disabled={busyStatus} onClick={() => updateRoomStatus('closed')}>Encerrar atendimento</Button>
          </div>
          <div className="stack-sm">
            {messages.filter((entry) => !shouldHideEntry(entry)).map((entry) => {
              const senderLabel = getVisibleSenderLabel(entry, user)
              const role = String(entry?.senderRole || '').toLowerCase()
              const cardClass = role === 'tutor_ia' ? 'surface-card compact border-info' : 'surface-card compact'
              return (
                <div key={entry.id} className={cardClass}>
                  {senderLabel ? <strong>{senderLabel}</strong> : null}
                  <p>{entry.message}</p>
                </div>
              )
            })}
          </div>
          {selectedRoom.status !== 'closed' ? (
            <div className="grid cols-2 mt-4">
              <textarea className="input textarea" placeholder="Escreva sua mensagem para o tutor e para o profissional." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} />
              <div className="button-wrap">
                <Button onClick={sendMessage}>Enviar mensagem</Button>
              </div>
            </div>
          ) : <div className="alert info mt-4">Atendimento concluído. Você ainda pode ler o histórico salvo.</div>}
        </Card>
        </>
      ) : null}
    </div>
  )
}
